import { SafetyCertificateOutlined } from '@ant-design/icons';
import { useIntl, useModel } from '@umijs/max';
import {
  Alert,
  App,
  Button,
  Form,
  Input,
  Modal,
  QRCode,
  Space,
  Spin,
  Typography,
} from 'antd';
import React, { useEffect, useState } from 'react';
import {
  disableOtp,
  enableOtp,
  getOtpQrCode,
} from '@/services/ant-design-pro/api';

type OtpBindingModalProps = {
  open: boolean;
  onCancel: () => void;
};

type EnableFormValues = {
  code: string;
};

type DisableFormValues = {
  password: string;
  username?: string;
};

const isSuccessfulResult = (result: API.CommonResult) => {
  if (result.success === true) {
    return true;
  }
  if (typeof result.status === 'string') {
    return ['success', 'ok'].includes(result.status.toLowerCase());
  }
  return false;
};

const getErrorMessage = (error: any, fallback: string) =>
  error?.info?.errorMessage ||
  error?.response?.data?.errorMessage ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const getOtpAuthValue = (data?: API.OtpQrCodeData) => {
  if (data?.otpAuthUri) {
    return data.otpAuthUri;
  }
  if (data?.qrCodeUrl) {
    try {
      const parsedUrl = new URL(data.qrCodeUrl);
      const encodedValue = parsedUrl.searchParams.get('data');
      if (encodedValue) {
        return decodeURIComponent(encodedValue);
      }
    } catch (_error) {
      return data.qrCodeUrl;
    }
  }
  if (data?.secretKey) {
    return `otpauth://totp/Aegis%20MDM?secret=${data.secretKey}`;
  }
  return undefined;
};

const OtpBindingModal: React.FC<OtpBindingModalProps> = ({
  open,
  onCancel,
}) => {
  const intl = useIntl();
  const { message } = App.useApp();
  const { initialState, setInitialState } = useModel('@@initialState');
  const [enableForm] = Form.useForm<EnableFormValues>();
  const [disableForm] = Form.useForm<DisableFormValues>();
  const [loadingQr, setLoadingQr] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [otpData, setOtpData] = useState<API.OtpQrCodeData>();

  const currentUser = initialState?.currentUser;
  const otpEnabled = Boolean(currentUser?.otpEnabled);
  const qrValue = getOtpAuthValue(otpData);

  const refreshCurrentUser = async () => {
    const userInfo = await initialState?.fetchUserInfo?.();
    setInitialState((state) => ({
      ...state,
      currentUser: userInfo,
    }));
  };

  const loadQrCode = async () => {
    setLoadingQr(true);
    try {
      const result = await getOtpQrCode({ skipErrorHandler: true });
      if (!isSuccessfulResult(result)) {
        Modal.error({
          title: intl.formatMessage({
            id: 'app.settings.security.otp-load-failed',
            defaultMessage: '获取 OTP 二维码失败',
          }),
          content:
            result.message ||
            intl.formatMessage({
              id: 'app.settings.security.otp-load-failed',
              defaultMessage: '获取 OTP 二维码失败',
            }),
        });
        return;
      }
      setOtpData(result.data);
    } catch (error) {
      Modal.error({
        title: intl.formatMessage({
          id: 'app.settings.security.otp-load-failed',
          defaultMessage: '获取 OTP 二维码失败',
        }),
        content: getErrorMessage(
          error,
          intl.formatMessage({
            id: 'app.settings.security.otp-load-failed',
            defaultMessage: '获取 OTP 二维码失败',
          }),
        ),
      });
    } finally {
      setLoadingQr(false);
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    if (!otpEnabled) {
      loadQrCode();
    }
  }, [open, otpEnabled]);

  const handleEnable = async () => {
    const values = await enableForm.validateFields();
    setSubmitting(true);
    try {
      const result = await enableOtp(values, { skipErrorHandler: true });
      if (!isSuccessfulResult(result)) {
        Modal.error({
          title: intl.formatMessage({
            id: 'app.settings.security.otp-enable-failed',
            defaultMessage: '启用 OTP 失败',
          }),
          content:
            result.message ||
            intl.formatMessage({
              id: 'app.settings.security.otp-enable-failed',
              defaultMessage: '启用 OTP 失败',
            }),
        });
        return;
      }
      message.success(
        result.message ||
          intl.formatMessage({
            id: 'app.settings.security.otp-enable-success',
            defaultMessage: 'OTP 已启用',
          }),
      );
      await refreshCurrentUser();
      onCancel();
    } catch (error) {
      Modal.error({
        title: intl.formatMessage({
          id: 'app.settings.security.otp-enable-failed',
          defaultMessage: '启用 OTP 失败',
        }),
        content: getErrorMessage(
          error,
          intl.formatMessage({
            id: 'app.settings.security.otp-enable-failed',
            defaultMessage: '启用 OTP 失败',
          }),
        ),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async () => {
    const values = await disableForm.validateFields();
    setSubmitting(true);
    try {
      const result = await disableOtp(values, { skipErrorHandler: true });
      if (!isSuccessfulResult(result)) {
        Modal.error({
          title: intl.formatMessage({
            id: 'app.settings.security.otp-disable-failed',
            defaultMessage: '禁用 OTP 失败',
          }),
          content:
            result.message ||
            intl.formatMessage({
              id: 'app.settings.security.otp-disable-failed',
              defaultMessage: '禁用 OTP 失败',
            }),
        });
        return;
      }
      message.success(
        result.message ||
          intl.formatMessage({
            id: 'app.settings.security.otp-disable-success',
            defaultMessage: 'OTP 已禁用',
          }),
      );
      await refreshCurrentUser();
      onCancel();
    } catch (error) {
      Modal.error({
        title: intl.formatMessage({
          id: 'app.settings.security.otp-disable-failed',
          defaultMessage: '禁用 OTP 失败',
        }),
        content: getErrorMessage(
          error,
          intl.formatMessage({
            id: 'app.settings.security.otp-disable-failed',
            defaultMessage: '禁用 OTP 失败',
          }),
        ),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      afterOpenChange={(visible) => {
        if (!visible) {
          enableForm.resetFields();
          disableForm.resetFields();
          setOtpData(undefined);
          setLoadingQr(false);
          setSubmitting(false);
        }
      }}
      destroyOnHidden
      forceRender
      open={open}
      title={intl.formatMessage({
        id: 'app.settings.security.otp-title',
        defaultMessage: 'OTP 管理',
      })}
      footer={null}
      onCancel={onCancel}
    >
      {otpEnabled ? (
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            showIcon
            title={intl.formatMessage({
              id: 'app.settings.security.otp-enabled-title',
              defaultMessage: 'OTP 已启用',
            })}
            description={intl.formatMessage({
              id: 'app.settings.security.otp-enabled-description',
              defaultMessage:
                '当前账户已绑定 OTP 设备。如需解绑，请输入当前密码。',
            })}
            type="success"
          />
          <Form<DisableFormValues>
            form={disableForm}
            layout="vertical"
            requiredMark={false}
          >
            <Form.Item
              hidden
              initialValue={currentUser?.userName || currentUser?.name || ''}
              name="username"
            >
              <Input autoComplete="username" />
            </Form.Item>
            <Form.Item
              label={intl.formatMessage({
                id: 'app.settings.security.otp-disable-password',
                defaultMessage: '当前密码',
              })}
              name="password"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'app.settings.security.otp-disable-password-required',
                    defaultMessage: '请输入当前密码以禁用 OTP',
                  }),
                },
              ]}
            >
              <Input.Password
                autoComplete="current-password"
                placeholder={intl.formatMessage({
                  id: 'app.settings.security.otp-disable-password-placeholder',
                  defaultMessage: '请输入当前密码',
                })}
              />
            </Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={onCancel}>
                {intl.formatMessage({
                  id: 'app.settings.security.cancel',
                  defaultMessage: '取消',
                })}
              </Button>
              <Button
                danger
                icon={<SafetyCertificateOutlined />}
                loading={submitting}
                type="primary"
                onClick={handleDisable}
              >
                {intl.formatMessage({
                  id: 'app.settings.security.otp-disable',
                  defaultMessage: '禁用 OTP',
                })}
              </Button>
            </Space>
          </Form>
        </Space>
      ) : (
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Typography.Paragraph type="secondary">
            {intl.formatMessage({
              id: 'app.settings.security.otp-setup-description',
              defaultMessage:
                '使用 Google Authenticator 或 Microsoft Authenticator 扫描二维码，然后输入 6 位验证码完成绑定。',
            })}
          </Typography.Paragraph>
          {loadingQr ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Spin />
            </div>
          ) : (
            <>
              {qrValue ? (
                <div style={{ textAlign: 'center' }}>
                  <QRCode errorLevel="M" size={220} value={qrValue} />
                </div>
              ) : (
                <Alert
                  showIcon
                  type="warning"
                  title={intl.formatMessage({
                    id: 'app.settings.security.otp-qr-unavailable',
                    defaultMessage: '二维码暂时不可用，请使用手动密钥绑定',
                  })}
                />
              )}
              {otpData?.manualEntryKey && (
                <Typography.Paragraph
                  copyable={{ text: otpData.manualEntryKey }}
                  strong
                >
                  {intl.formatMessage({
                    id: 'app.settings.security.otp-manual-key',
                    defaultMessage: '手动输入密钥',
                  })}
                  ：{otpData.manualEntryKey}
                </Typography.Paragraph>
              )}
              <Form<EnableFormValues>
                form={enableForm}
                layout="vertical"
                requiredMark={false}
              >
                <Form.Item
                  label={intl.formatMessage({
                    id: 'app.settings.security.otp-code',
                    defaultMessage: '6 位验证码',
                  })}
                  name="code"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: 'app.settings.security.otp-code-required',
                        defaultMessage: '请输入 6 位验证码',
                      }),
                    },
                    {
                      pattern: /^\d{6}$/,
                      message: intl.formatMessage({
                        id: 'app.settings.security.otp-code-invalid',
                        defaultMessage: '验证码必须为 6 位数字',
                      }),
                    },
                  ]}
                >
                  <Input
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder={intl.formatMessage({
                      id: 'app.settings.security.otp-code-placeholder',
                      defaultMessage: '请输入验证码',
                    })}
                  />
                </Form.Item>
                <Space
                  style={{ width: '100%', justifyContent: 'space-between' }}
                >
                  <Button onClick={loadQrCode}>
                    {intl.formatMessage({
                      id: 'app.settings.security.otp-refresh-qr',
                      defaultMessage: '刷新二维码',
                    })}
                  </Button>
                  <Space>
                    <Button onClick={onCancel}>
                      {intl.formatMessage({
                        id: 'app.settings.security.cancel',
                        defaultMessage: '取消',
                      })}
                    </Button>
                    <Button
                      loading={submitting}
                      type="primary"
                      onClick={handleEnable}
                    >
                      {intl.formatMessage({
                        id: 'app.settings.security.otp-enable',
                        defaultMessage: '启用 OTP',
                      })}
                    </Button>
                  </Space>
                </Space>
              </Form>
            </>
          )}
        </Space>
      )}
    </Modal>
  );
};

export default OtpBindingModal;
